import {
  Add,
  ArrowDropDown,
  Check,
  Close,
  Edit
} from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import CustomIconButton from "./CustomIconButton";
import TableLoader from "./TableLoader";

const EditableTable = ({
  data,
  headerTabs,
  subRowsKey = "sub_rows",
  isLoading,
  editableFields,
  action,
  handleUpdateEntity,
  handleAddSubEntity,
  isAdmin,
  type,
  subTableName = "",
  hasSubRows,
}) => {
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [localData, setLocalData] = useState([]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEditClick = (row) => {
    setEditingRowId(row.id);
    setEditingValues({ ...row });
  };

  const handleSaveClick = async (row, parentRow = null) => {
    if (row.isNew) {
      await handleAddSubEntity({ name: editingValues.name, description: editingValues.description }, parentRow);
      if (parentRow) {
        const updatedSubRows = (parentRow[subRowsKey] || []).filter(r => !r.isNew);
        setLocalData(prevData =>
          prevData.map(r => (r.id === parentRow.id ? { ...r, [subRowsKey]: updatedSubRows } : r))
        );
      }
    } else {
      await handleUpdateEntity(row.id, editingValues, !!parentRow);
    }

    setEditingRowId(null);
    setEditingValues({});
  };

  const handleCancelClick = (parentRow) => {
    if (!parentRow) {
      setEditingRowId(null);
      setEditingValues({});
      return;
    }

    parentRow[subRowsKey] = parentRow[subRowsKey].filter(r => !r.isNew);
    setEditingRowId(null);
    setEditingValues({});
    setLocalData([...localData]);
  };

  const handleAddBlankSubRow = (parentRow) => {
    const hasTempRow = (parentRow[subRowsKey] || []).some(r => r.isNew);
    if (hasTempRow) return;

    const newRow = {
      // id: `temp-${Date.now()}`, // keep unique id
      name: "",
      isNew: true,
      parentId: parentRow.id,
    };

    parentRow[subRowsKey] = [...(parentRow[subRowsKey] || []), newRow];
    setExpandedRows((prev) => ({ ...prev, [parentRow.id]: true }));
    setEditingRowId(newRow.id);
    setEditingValues(newRow);
    setLocalData([...localData]);
  };

  const handleInputChange = (e, key) => {
    setEditingValues(prev => ({ ...prev, [key]: e.target.value }));
  };

  const renderCell = (row, column) => {
    const value =
      editingRowId === row.id
        ? editingValues[column.key] ?? row[column.key]
        : row[column.key];

    if (editingRowId === row.id && editableFields.includes(column.key)) {
      return (
        <Form.Control
          size="sm"
          value={value}
          onChange={(e) => handleInputChange(e, column.key)}
        />
      );
    }
    if (column.key === "color") {
      if (editingRowId === row.id) {
        return (
          <Form.Control
            type="color"
            value={value}
            onChange={(e) => handleInputChange(e, column.key)}
          />
        );
      }

      return (
        <div
          style={{
            width: "40px",
            height: "30px",
            backgroundColor: row.color,
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        ></div>
      );
    }
    return value || "—";
  };

  const renderActionButtons = (row, parentRow = null) => {
    if (!isAdmin) return null;
    const canEdit = row.gainss_project_id !== null;

    if (!canEdit) return action ? action(row) : null;

    const buttons = editingRowId === row.id ? (
      <>
        <CustomIconButton
          title="Save"
          disableRipple
          disabled={!editingValues.name}
          onClick={() => handleSaveClick(row, parentRow)}
        >
          <Check sx={{ color: "green" }} />
        </CustomIconButton>
        <CustomIconButton
          title="Cancel"
          disableRipple
          onClick={() => handleCancelClick(parentRow)}
        >
          <Close />
        </CustomIconButton>
      </>
    ) : (
      <>
        <CustomIconButton
          title={type === "jobs" ? "Can't be modified" : "Edit"}
          disableRipple
          onClick={() => handleEditClick(row)}
          disabled={row.is_default}
        >
          <Edit />
        </CustomIconButton>
        {action && action(row)}
      </>
    );

    if (!parentRow) {
      return (
        <>
          {buttons}
         {hasSubRows && (
          <CustomIconButton
            title={`Add ${subTableName}`}
            disableRipple
            onClick={() => handleAddBlankSubRow(row)}
          >
            <Add />
          </CustomIconButton>
        )}
        </>
      );
    }

    return buttons;
  };


  const renderRows = (rows, parentRow = null, level = 0) => {
    return rows.flatMap(row => {
      const rowElement = (
        <tr key={row.id}>
          {hasSubRows && (
            <td align="center" style={{ paddingLeft: level * 20 }}>
              {row[subRowsKey]?.length > 0 && (
                <IconButton
                  onClick={() => toggleRow(row.id)}
                  disableRipple
                  size="small"
                >
                  <ArrowDropDown
                    style={{
                      transform: expandedRows[row.id] ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              )}
            </td>
          )}

          {headerTabs.map(col => (
            <td key={col.key}>{renderCell(row, col)}</td>
          ))}

          {isAdmin && (
            <td style={{ whiteSpace: "nowrap", width: "5%" }}>
              {renderActionButtons(row, parentRow)}
            </td>
          )}
        </tr>
      );

      const subRows = expandedRows[row.id] && row[subRowsKey]?.length
        ? renderRows(row[subRowsKey], row, level + 1)
        : [];

      return [rowElement, ...subRows];
    });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Table bordered>
          <thead>
            <tr>
              {hasSubRows && <th style={{ width: "5%" }} />}
              {headerTabs.map(tab => (
                <th key={tab.key}>{tab.label}</th>
              ))}
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <TableLoader colSpan={headerTabs.length + 2} />
            ) : localData.length > 0 ? (
              renderRows(localData)
            ) : (
              <tr>
                <td colSpan={headerTabs.length + 2} align="center">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Grid>
    </Grid>
  );
};

export default EditableTable;
